import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Load-test console (part 24).
 *
 * The API measures itself: a bounded worker pool issues real GET requests to one of three
 * read-only paths and returns nearest-rank percentiles. This screen only starts runs and reads
 * reports; the arithmetic lives in the Go domain package so the number shown here is the number
 * stored in platform.load_runs.
 */

type Verdict = 'pass' | 'fail' | 'inconclusive'
type RunStatus = 'running' | 'passed' | 'failed' | 'error'

interface LoadRunRow {
  id: string
  path: string
  method: string
  concurrency: number
  request_count: number
  ramp_seconds: number
  status: RunStatus
  verdict: Verdict
  started_at: string
  finished_at?: string
  duration_ms: number
  success_count: number
  error_count: number
  error_rate: number
  rps: number
  p50_ms: number
  p90_ms: number
  p95_ms: number
  p99_ms: number
  min_ms: number
  max_ms: number
  mean_ms: number
  threshold_p95_ms: number
  threshold_error_rate: number
  failure_reasons: string[]
  created_by?: string
}

interface LoadSample {
  sequence: number
  status_code: number
  latency_ms: number
  ok: boolean
  error_kind?: string
  error_detail?: string
  offset_ms: number
}

interface LoadReport {
  run: LoadRunRow
  samples: LoadSample[]
}

const PATHS: { value: string; label: string }[] = [
  { value: '/healthz', label: '/healthz · ตรวจสุขภาพพื้นฐาน' },
  { value: '/readyz', label: '/readyz · ตรวจความพร้อมของบริการ' },
  { value: '/version', label: '/version · เวอร์ชันที่กำลังให้บริการ' },
]

const VERDICT_LABEL: Record<Verdict, string> = {
  pass: 'ผ่านเกณฑ์',
  fail: 'ไม่ผ่านเกณฑ์',
  inconclusive: 'ยังไม่มีข้อสรุป',
}

const VERDICT_TONE: Record<Verdict, string> = {
  pass: 'sev-low',
  fail: 'sev-critical',
  inconclusive: 'sev-medium',
}

const STATUS_LABEL: Record<RunStatus, string> = {
  running: 'กำลังทดสอบ',
  passed: 'เสร็จสิ้น · ผ่าน',
  failed: 'เสร็จสิ้น · ไม่ผ่าน',
  error: 'ผิดพลาด',
}

const FAILURE_LABEL: Record<string, string> = {
  no_samples: 'ไม่มีคำขอที่วัดผลได้',
  error_rate_exceeded: 'อัตราข้อผิดพลาดสูงกว่าเกณฑ์',
  p95_exceeded: 'p95 ช้ากว่าเกณฑ์',
  run_failed: 'การทดสอบถูกยกเลิกก่อนเสร็จ',
}

function msText(value: number): string {
  if (!Number.isFinite(value)) return '—'
  if (value >= 1000) return `${(value / 1000).toFixed(2)} วินาที`
  return `${value.toFixed(2)} ms`
}

function rateText(value: number): string {
  return `${(value * 100).toFixed(2)}%`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function intValue(raw: string, fallback: number): number {
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : fallback
}

export function LoadTest() {
  const qc = useQueryClient()
  const [path, setPath] = useState('/healthz')
  const [concurrency, setConcurrency] = useState('16')
  const [requests, setRequests] = useState('200')
  const [rampSeconds, setRampSeconds] = useState('0')
  const [thresholdP95, setThresholdP95] = useState('750')
  const [thresholdRatePct, setThresholdRatePct] = useState('1')
  const [selected, setSelected] = useState<string | null>(null)
  const [liveReport, setLiveReport] = useState<LoadReport | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const runs = useQuery({
    queryKey: ['loadtest-runs'],
    queryFn: () => api<LoadRunRow[]>('/v1/loadtest/runs'),
    refetchInterval: 15_000,
    staleTime: 5_000,
  })

  const reportQ = useQuery({
    queryKey: ['loadtest-run', selected],
    queryFn: () => api<LoadReport>(`/v1/loadtest/runs/${encodeURIComponent(selected ?? '')}`),
    enabled: selected !== null && liveReport?.run.id !== selected,
  })

  const start = useMutation({
    mutationFn: () =>
      apiWrite<LoadReport>('/v1/loadtest/runs', 'POST', {
        path,
        concurrency: intValue(concurrency, 0),
        requests: intValue(requests, 0),
        ramp_seconds: intValue(rampSeconds, 0),
        threshold_p95_ms: intValue(thresholdP95, 0),
        threshold_error_rate: Math.max(0, Math.min(1, Number(thresholdRatePct) / 100)),
      }),
    onSuccess: (report) => {
      setActionError(null)
      setLiveReport(report)
      setSelected(report.run.id)
      setNotice(
        `ทดสอบ ${report.run.path} เสร็จแล้ว · ${number(report.run.request_count)} คำขอ · ` +
        `p95 ${msText(report.run.p95_ms)} · ${VERDICT_LABEL[report.run.verdict]}`,
      )
      void qc.invalidateQueries({ queryKey: ['loadtest-runs'] })
    },
    onError: (err) => {
      setActionError(errorText(err, 'เริ่มการทดสอบไม่สำเร็จ'))
    },
  })

  const report = liveReport && liveReport.run.id === selected ? liveReport : reportQ.data
  const rows = runs.data ?? []
  const latest = report?.run ?? rows[0]

  const samples = useMemo(() => {
    if (!report) return []
    return report.samples.slice(0, 20)
  }, [report])

  const busy = start.isPending
  const canStart =
    !busy && path !== '' && intValue(concurrency, 0) >= 1 && intValue(requests, 0) >= 1

  return (
    <>
      <PageHead
        title="ทดสอบภาระงานและวัดประสิทธิภาพ"
        sub="ยิงคำขอ GET จริงเข้าสู่ API ผ่านพูลคนงานที่มีขอบเขต แล้วสรุปเวลาแฝงด้วย nearest-rank · รองรับเฉพาะเส้นทางอ่านอย่างเดียว"
        actions={
          <span className="dim" style={{ fontSize: 11.5 }}>
            {latest ? `ทดสอบล่าสุด ${thaiDateTime(latest.started_at)}` : 'ยังไม่มีการทดสอบ'}
          </span>
        }
      />

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 14, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {actionError ? (
        <div className="card card-pad" role="alert" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>{actionError}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat label="p50" value={latest ? msText(latest.p50_ms) : '—'} foot="ค่ามัธยฐานของคำขอที่วัดได้" />
        <Stat label="p95" value={latest ? msText(latest.p95_ms) : '—'} foot="เกณฑ์ตัดสินค่าเริ่มต้น 750 ms" />
        <Stat label="p99" value={latest ? msText(latest.p99_ms) : '—'} foot="หางการกระจาย ไม่ใช่ค่าเฉลี่ย" />
        <Stat
          label="อัตราข้อผิดพลาด"
          value={latest ? rateText(latest.error_rate) : '—'}
          foot={latest ? `${number(latest.error_count)} จาก ${number(latest.request_count)} คำขอ` : 'ยังไม่มีข้อมูล'}
        />
      </div>

      <div className="grid-2" style={{ marginTop: 18, alignItems: 'start' }}>
        <div className="card card-pad">
          <div className="card-label">ตั้งค่าการทดสอบ</div>
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="loadtest-path">เส้นทางเป้าหมาย</label>
            <select
              id="loadtest-path"
              className="select"
              style={{ width: '100%' }}
              value={path}
              onChange={(e) => setPath(e.target.value)}
            >
              {PATHS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="loadtest-concurrency">จำนวนคนงาน (1–64)</label>
              <input
                id="loadtest-concurrency"
                className="input"
                type="number"
                min={1}
                max={64}
                value={concurrency}
                onChange={(e) => setConcurrency(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="loadtest-requests">จำนวนคำขอ (1–5000)</label>
              <input
                id="loadtest-requests"
                className="input"
                type="number"
                min={1}
                max={5000}
                value={requests}
                onChange={(e) => setRequests(e.target.value)}
              />
            </div>
          </div>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="loadtest-ramp">ค่อย ๆ เพิ่มคนงาน (วินาที)</label>
              <input
                id="loadtest-ramp"
                className="input"
                type="number"
                min={0}
                max={300}
                value={rampSeconds}
                onChange={(e) => setRampSeconds(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="loadtest-p95">เกณฑ์ p95 (ms)</label>
              <input
                id="loadtest-p95"
                className="input"
                type="number"
                min={1}
                max={60000}
                value={thresholdP95}
                onChange={(e) => setThresholdP95(e.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="loadtest-rate">เกณฑ์อัตราข้อผิดพลาด (%)</label>
            <input
              id="loadtest-rate"
              className="input"
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={thresholdRatePct}
              onChange={(e) => setThresholdRatePct(e.target.value)}
            />
          </div>

          <div className="row" style={{ marginTop: 16, gap: 10 }}>
            <button className="btn btn-primary" disabled={!canStart} onClick={() => start.mutate()}>
              {busy ? 'กำลังทดสอบ…' : 'เริ่มทดสอบ'}
            </button>
            <span className="dim" style={{ fontSize: 11.5 }}>
              คำขอเป็น GET จริงไปยัง API นี้ · ต้องมี Idempotency-Key
            </span>
          </div>
          {busy ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              กำลังยิง {number(intValue(requests, 0))} คำขอด้วยคนงาน {number(intValue(concurrency, 0))} ตัว
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">สรุปการทดสอบที่เลือก</div>
          {report ? (
            <>
              <div className="row" style={{ marginTop: 12, gap: 8 }}>
                <span className="mono" style={{ fontSize: 12 }}>{report.run.path}</span>
                <span className={`sev ${VERDICT_TONE[report.run.verdict]}`}>
                  <i className="sev-dot" />
                  {VERDICT_LABEL[report.run.verdict]}
                </span>
              </div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                {thaiDateTime(report.run.started_at)} · {number(report.run.request_count)} คำขอ · คนงาน {report.run.concurrency}
                {report.run.ramp_seconds > 0 ? ` · ramp ${report.run.ramp_seconds} วินาที` : ''}
              </div>

              <div className="grid-2" style={{ marginTop: 16 }}>
                <div>
                  <div className="card-label">เวลาแฝง</div>
                  <div className="stack" style={{ marginTop: 8 }}>
                    {[
                      ['p50', report.run.p50_ms],
                      ['p90', report.run.p90_ms],
                      ['p95', report.run.p95_ms],
                      ['p99', report.run.p99_ms],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="row" style={{ justifyContent: 'space-between' }}>
                        <span className="dim" style={{ fontSize: 12 }}>{label}</span>
                        <span className="tnum" style={{ fontSize: 12.5 }}>{msText(Number(value))}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="card-label">ปริมาณ</div>
                  <div className="stack" style={{ marginTop: 8 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>RPS</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{report.run.rps.toFixed(2)}</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>เวลารวม</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{msText(report.run.duration_ms)}</span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>ต่ำสุด / สูงสุด</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>
                        {msText(report.run.min_ms)} / {msText(report.run.max_ms)}
                      </span>
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="dim" style={{ fontSize: 12 }}>เกณฑ์ p95</span>
                      <span className="tnum" style={{ fontSize: 12.5 }}>{msText(report.run.threshold_p95_ms)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {report.run.failure_reasons.length > 0 ? (
                <div className="row" style={{ gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
                  {report.run.failure_reasons.map((code) => (
                    <span key={code} className="chip">{FAILURE_LABEL[code] ?? code}</span>
                  ))}
                </div>
              ) : null}
            </>
          ) : selected ? (
            <div style={{ marginTop: 12 }}><Skel h={120} /></div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <Empty
                title="ยังไม่ได้เลือกผลการทดสอบ"
                body="เริ่มการทดสอบใหม่ หรือเลือกแถวจากตารางด้านล่างเพื่อดูรายละเอียด percentile และตัวอย่างคำขอ"
              />
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
          <span className="card-label">ประวัติการทดสอบ</span>
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => void runs.refetch()} disabled={runs.isFetching}>
            {runs.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        </div>

        {runs.isLoading ? (
          <div style={{ padding: 20 }} className="stack"><Skel h={44} /><Skel h={44} /><Skel h={44} /></div>
        ) : runs.isError ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="โหลดประวัติการทดสอบไม่สำเร็จ"
              body={errorText(runs.error, 'เรียก /v1/loadtest/runs ไม่สำเร็จ — ตรวจสอบว่าเส้นทางถูก mount แล้ว')}
            />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="ยังไม่มีการทดสอบที่บันทึกไว้"
              body="เมื่อเริ่มการทดสอบ ระบบจะบันทึกหัวการทดสอบและตัวอย่างคำขอลง platform.load_runs และ platform.load_samples"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>เส้นทาง</th>
                  <th className="num">คำขอ</th>
                  <th className="num">p50</th>
                  <th className="num">p95</th>
                  <th className="num">p99</th>
                  <th className="num">ผิดพลาด</th>
                  <th>ผล</th>
                  <th>เริ่ม</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} onClick={() => setSelected(row.id)}
                    style={row.id === selected ? { background: 'rgba(200, 169, 106, 0.06)' } : undefined}>
                    <td>
                      <div className="mono" style={{ fontSize: 12 }}>{row.path}</div>
                      <div className="dim" style={{ fontSize: 11 }}>
                        คนงาน {row.concurrency}{row.ramp_seconds > 0 ? ` · ramp ${row.ramp_seconds}s` : ''}
                      </div>
                    </td>
                    <td className="num">{number(row.request_count)}</td>
                    <td className="num">{msText(row.p50_ms)}</td>
                    <td className="num">{msText(row.p95_ms)}</td>
                    <td className="num">{msText(row.p99_ms)}</td>
                    <td className="num">{number(row.error_count)}</td>
                    <td>
                      <span className={`sev ${VERDICT_TONE[row.verdict]}`}>
                        <i className="sev-dot" />
                        {STATUS_LABEL[row.status]}
                      </span>
                    </td>
                    <td className="dim" style={{ fontSize: 11.5 }}>{thaiDateTime(row.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {report && report.samples.length > 0 ? (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
            <span className="card-label">ตัวอย่างคำขอ</span>
            <span className="dim" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
              แสดง {samples.length} จาก {number(report.samples.length)} รายการ
            </span>
          </div>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th className="num">ลำดับ</th>
                  <th className="num">สถานะ HTTP</th>
                  <th className="num">เวลาแฝง</th>
                  <th className="num">เริ่มที่</th>
                  <th>ผล</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((sample) => (
                  <tr key={sample.sequence}>
                    <td className="num">{sample.sequence}</td>
                    <td className="num">{sample.status_code === 0 ? '—' : sample.status_code}</td>
                    <td className="num">{msText(sample.latency_ms)}</td>
                    <td className="num">{msText(sample.offset_ms)}</td>
                    <td>
                      {sample.ok ? (
                        <span className="sev sev-low"><i className="sev-dot" />สำเร็จ</span>
                      ) : (
                        <span className="sev sev-critical" title={sample.error_detail}>
                          <i className="sev-dot" />{sample.error_kind === 'status' ? sample.error_detail : 'เชื่อมต่อไม่สำเร็จ'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  )
}
